import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getInviteTokenInfo, setPasswordFromInvite } from '../../Helpers/dataApi';
import PasswordStrengthIndicator from '../../Components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import { useAuth } from '../../Contexts/AuthContext';
import PageLayout from '../../Components/PageLayout/PageLayout';

export default function SetPassword() {
  const history = useHistory();
  const auth = useAuth();
  const redirectTimeout = useRef(null);

  // Token lookup state
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    password: { error: false, message: '' },
    confirmPassword: { error: false, message: '' },
  });
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');

  // On mount, clear any existing session before doing anything else.
  // An admin may be setting up an account for someone else while they
  // themselves are already logged in. Without this, in-flight requests
  // made with the admin's token get 401'd when the cookie swaps over
  // during auth.login(), which triggers the global session-expired redirect.
  useEffect(() => {
    if (auth.activeUser) {
      auth.logout();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount, validate the token and fetch user info
  useEffect(() => {
    if (!token) {
      setTokenError(true);
      setTokenLoading(false);
      return;
    }

    getInviteTokenInfo(token)
      .then((response) => {
        setUserInfo(response.data);
        setTokenLoading(false);
      })
      .catch(() => {
        setTokenError(true);
        setTokenLoading(false);
      });

    return () => {
      if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
    };
  }, []);

  const validatePasswords = (pwd, confirmPwd) => {
    const validation = {
      password: { error: false, message: '' },
      confirmPassword: { error: false, message: '' },
    };

    if (!pwd) {
      validation.password.error = true;
      validation.password.message = 'Please enter a password';
      return validation;
    }
    if (!confirmPwd) {
      validation.confirmPassword.error = true;
      validation.confirmPassword.message = 'Please confirm your password';
      return validation;
    }
    if (pwd.length < 8) {
      validation.password.error = true;
      validation.password.message = 'Password must be at least 8 characters';
      return validation;
    }
    if (!/[A-Z]/.test(pwd)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one uppercase letter';
      return validation;
    }
    if (!/[a-z]/.test(pwd)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one lowercase letter';
      return validation;
    }
    if (!/\d/.test(pwd)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one number';
      return validation;
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/;'`~]/.test(pwd)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one special character';
      return validation;
    }
    if (pwd !== confirmPwd) {
      validation.password.error = true;
      validation.confirmPassword.error = true;
      validation.password.message = 'Passwords do not match';
      validation.confirmPassword.message = 'Passwords do not match';
    }

    return validation;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validatePasswords(password, confirmPassword);
    setErrors(validationErrors);
    setFeedback(null);

    if (validationErrors.password.error || validationErrors.confirmPassword.error) return;

    setLoading(true);
    try {
      const response = await setPasswordFromInvite(token, password);
      // Use auth.login to get the full user profile and store it in the activeUser cookie.
      const loginSucceeded = await auth.login(userInfo.email, password);

      if (loginSucceeded) {
        setFeedback({
          severity: 'success',
          message: 'Password set successfully. Redirecting...',
        });
        redirectTimeout.current = setTimeout(() => history.push('/project-select'), 1500);
      } else {
        setFeedback({
          severity: 'error',
          message: 'Password was set successfully, but automatic sign-in failed. Please sign in manually.',
        });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.error || 'An error occurred. Please try again.';
      let userMessage = errorMsg;
      if (errorMsg.includes('expired') || errorMsg.includes('Invalid') || errorMsg.includes('invalid')) {
        userMessage = 'This invite link is invalid or has expired. Please contact your administrator.';
      }
      setFeedback({ severity: 'error', message: userMessage });
    } finally {
      setLoading(false);
    }
  };

  // Token loading state
  if (tokenLoading) {
    return (
      <PageLayout maxWidth="xs">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // Invalid / expired token state
  if (tokenError) {
    return (
      <PageLayout maxWidth="xs">
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', mt: 8 }}>
          <Avatar sx={{ m: 1, bgcolor: 'error.main' }}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5" gutterBottom>
            Invalid Invite Link
          </Typography>
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            This invite link is invalid or has expired. Please contact your administrator to resend the invite.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="xs">
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', mt: 8 }}>
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Set Your Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          Welcome, {userInfo.first_name} {userInfo.last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {userInfo.email}
        </Typography>

        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
          <TextField
            autoComplete="new-password"
            autoFocus
            error={errors.password.error}
            fullWidth
            helperText={errors.password.message}
            id="password"
            label="Password"
            margin="normal"
            name="password"
            onChange={(e) => setPassword(e.target.value)}
            required
            type={showPassword ? 'text' : 'password'}
            value={password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthIndicator password={password} />
          <TextField
            autoComplete="new-password"
            error={errors.confirmPassword.error}
            fullWidth
            helperText={errors.confirmPassword.message}
            id="confirmPassword"
            label="Confirm Password"
            margin="normal"
            name="confirmPassword"
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            disabled={loading}
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            type="submit"
            variant="contained"
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Password'}
          </Button>
          {feedback && (
            <Collapse in={!!feedback} sx={{ width: '100%' }}>
              <Alert severity={feedback.severity} sx={{ mt: '0.5rem', textAlign: 'center' }}>
                {feedback.message}
              </Alert>
            </Collapse>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}
